import {error} from '@services/AlertService.js';
import {HTTP_UNPROCESSABLE_ENTITY} from '@constants/ResponseStatus.js';
import {isAxiosError, AxiosError} from 'axios';
import dayjs from 'dayjs';

export const IS_PRODUCTION = document.head.querySelector('meta[name="app-env"]')?.content === 'production';

export const IS_DEBUG_MODE = document.head.querySelector('meta[name="app-dbg"]')?.content === '1';

const isServerError = (e) => {
    return e instanceof AxiosError;
};

export const debug = (...args) => {
  if (!IS_DEBUG_MODE) {
    return;
  }

  let hadNonServerErrors = false;

  args.forEach(e => {
      if (isServerError(e)) {
          return;
      }

      hadNonServerErrors = true;

      console.dir(e);
  });

  if (hadNonServerErrors) {
      console.trace('------ DEBUG TRACE ------');
  }
};

const isJsonResponse = (response) => {
  if (!response) {
    return false;
  }

  try {
    return response.headers['content-type'].includes('application/json');
  } catch (e) {
    return false;
  }
};

const getErrorMessageFromBlob = async (e) => {
  if (!(e instanceof AxiosError && isJsonResponse(e.response) && e.response?.data instanceof Blob)) {
    return null;
  }

  try {
    const json = JSON.parse(await e.response.data.text());

    if (json.message) {
      return json.message;
    }
  } catch (e) {
    console.error(e);

    return null;
  }
}

export const handleError = async (e, defaultMessage = 'Unexpected error occurred') => {
  if (!isAxiosError(e) || e.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
    debug(e);

    return e;
  }

  const message = await getErrorMessageFromBlob(e) ?? (e.response?.data?.message || defaultMessage);

  // noinspection JSIgnoredPromiseFromCall,ES6MissingAwait
  error({message});
  debug(e);
};

export const trim = (value) => {
  if (typeof value === 'string') {
    return value.trim();
  }

  return value;
};

export const lowercase = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase();
  }

  return value;
};

export const formatDate = (date, format = 'DD/MM/YYYY') => {
  return date ? dayjs(date).format(format) : null;
};

export const formatNumber = (number) => {
  const num = parseFloat(number);
  if (typeof num !== 'number' || isNaN(num) || !isFinite(num)) {
    console.error('Invalid numeric value encountered', number);
    return null;
  }

  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Capitalizes first letters of words in string.
 * @param {string} str String to be modified
 * @param {boolean=false} lower Whether all other letters should be lower-cased
 * @return {string}
 * @usage
 *   capitalize('fix this string'); // -> 'Fix This String'
 *   capitalize('javaSCrIPT'); // -> 'JavaSCrIPT'
 *   capitalize('javaSCrIPT', true); // -> 'Javascript'
 */
export const capitalize = (str, lower = false) => {
    return (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());
};

export const slugToLabel = (str, splitOn = '-') => {
    return str.split(splitOn).map(s => capitalize(s, true)).join(' ');
};

/**
 * @param {Blob} blob
 * @param {string} fileName
 * @param {string} type
 */
export const blobToFile = (blob, fileName, type = 'application/pdf') => {
    const file = new File([blob], fileName, {type});

    return {
        file,
        get url() {
            return URL.createObjectURL(file);
        },
    }
};

export const blobToJson = async (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            try {
                const json = JSON.parse(reader.result);
                resolve(json);
            } catch (error) {
                reject(new Error('Failed to parse JSON'));
            }
        };

        reader.onerror = () => reject(new Error('Failed to read blob'));
        reader.readAsText(blob); // Read blob as text
    });
}

